import {
  DoubleRightOutlined,
  DownOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  Company,
  GET_TOP_CONTRIBUTORS,
  MostActiveUsers,
  User,
} from '@frontend/data-access';
import { UserAvatar, UserInfo } from '@frontend/shared/ui';
import { formatNumber } from '@frontend/shared/util';
import type { PaginationProps } from 'antd';
import {
  Button,
  Card,
  DatePicker,
  Drawer,
  Dropdown,
  Empty,
  Grid,
  Pagination,
  Segmented,
  Spin,
} from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import { SegmentedLabeledOption, SegmentedValue } from 'antd/lib/segmented';
import dayjs from 'dayjs';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useState } from 'react';

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

enum Tab {
  POINT = 'POINT',
  IDEA = 'IDEA',
  POST = 'POST',
  CHALLENGE = 'CHALLENGE',
  UPVOTE = 'UPVOTE',
  CONTRIBUTION = 'CONTRIBUTION',
  APPLICATION = 'APPLICATION',
}

const TopContributorsStats = () => {
  const { t } = useTranslation('admin');
  const { publicRuntimeConfig } = getConfig();
  const [tab, setTab] = useState<Tab>(Tab.POINT);
  const [current, setCurrent] = useState(1);
  const [drawer, setDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const { t: tcommon } = useTranslation('common');
  const [option, setOption] = useState({
    key: 'internal-users',
    label: t('internal_users'),
  });
  const screens = useBreakpoint();
  const pageSize = 10;

  const endDate = dayjs();
  const startDate = endDate.subtract(3, 'month');
  const [date, setDate] = useState([
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
  ]);

  const dropdownOptions = useMemo(
    () => [
      { key: 'internal-users', label: t('internal_users') },
      { key: 'external-users', label: t('external_users') },
    ],
    [t]
  );

  const internalSegmentOptions: (SegmentedValue | SegmentedLabeledOption)[] =
    useMemo(
      () => [
        { value: 'POINT', label: t('by_points') },
        { value: 'IDEA', label: t('idea') },
        { value: 'POST', label: t('post') },
        { value: 'CHALLENGE', label: t('intra_challenge') },
        { value: 'UPVOTE', label: t('upvote') },
        { value: 'CONTRIBUTION', label: t('contribution') },
      ],
      [t]
    );
  const externalSegmentOptions: (SegmentedValue | SegmentedLabeledOption)[] = [
    { value: 'APPLICATION', label: t('open_challenge_application') },
    { value: 'POST', label: t('post') },
  ];

  const [getTopUsers, { data, loading }] = useLazyQuery<{
    getMostActiveUsers: MostActiveUsers[];
  }>(GET_TOP_CONTRIBUTORS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (option.key === 'external-users') {
      setTab(Tab.APPLICATION);
    } else {
      setTab(Tab.POINT);
    }
  }, [option]);

  useEffect(() => {
    setCurrent(1);
    const variables = {
      input: {
        role:
          option.key === 'internal-users' ? 'INTRAPRENEUR' : 'EXTERNAL_USER',
        content: tab,
        startDate: date[0],
        endDate: date[1],
      },
    };
    getTopUsers({ variables });
  }, [date, getTopUsers, option.key, tab]);

  const onChange: PaginationProps['onChange'] = (page) => {
    setCurrent(page);
  };

  const showDrawer = (user: User) => {
    setSelectedUser(user);
    setDrawer(true);
  };

  const handleDrawer = () => {
    setDrawer(!drawer);
  };

  const renderCompany = (companies: Company[]) => {
    return companies.map((company, index) => {
      const companyName =
        company.name.length > 20
          ? company.name.slice(0, 20) + '...'
          : company.name;
      return (
        <a
          key={company.id}
          className="hover:text-primary-700 cursor-pointer"
          href={`/company-management/${company.id}`}
          target="_blank"
        >
          {companyName}
          {index < companies.length - 1 && ', '}
        </a>
      );
    });
  };

  const renderImage = (index: number) => {
    if (index > 3) {
      return (
        <div className="relative flex">
          <Image
            src={`${publicRuntimeConfig.staticFolder}/svg/leaderboard/default.svg`}
            height="30"
            width="30"
            alt="badge"
            className="min-w-[30px]"
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            {index}
          </div>
        </div>
      );
    } else {
      return (
        <Image
          src={`${publicRuntimeConfig.staticFolder}/svg/leaderboard/place-${index}.svg`}
          height="30"
          width="30"
          alt="badge"
          className="min-w-[30px]"
        />
      );
    }
  };

  const List = () => {
    if (loading) {
      return (
        <div className="p-5 text-center">
          <Spin />
        </div>
      );
    }
    if (!data?.getMostActiveUsers.length) {
      return (
        <div className="p-5 text-center">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    const startIndex = (current - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedUsers = data.getMostActiveUsers.slice(startIndex, endIndex);

    return (
      <div>
        <ul className="flex flex-col gap-4 mt-5">
          {paginatedUsers.map((item, index) => (
            <li key={index} className="flex items-start gap-3 w-full">
              <div className="flex-none">
                {renderImage(startIndex + index + 1)}
              </div>
              <div className="w-[40px] min-w-[40px] flex-none">
                <UserAvatar size={40} user={item?.user} showBadge />
              </div>
              <div className="flex flex-col flex-1 truncate">
                <div
                  className="hover:text-primary-700 cursor-pointer line-clamp-1"
                  onClick={() => showDrawer(item?.user)}
                >
                  {item?.user?.fullName}{' '}
                </div>
                <div className="text-gray-700 text-xs text-wrap">
                  {option.key === 'internal-users' ? (
                    `${formatNumber(item?.user?.rank?.rank)} ${tcommon(
                      'points'
                    )}`
                  ) : (
                    <div>
                      {tcommon('from')} {renderCompany(item?.user?.companies)}
                    </div>
                  )}
                </div>
              </div>
              <span className="flex-none py-1 px-4 text-xs bg-gray-400 rounded-full text-gray-900 font-normal whitespace-nowrap">
                {getLabel(item)}
              </span>
            </li>
          ))}
        </ul>
        {data?.getMostActiveUsers.length > pageSize && (
          <Pagination
            total={data?.getMostActiveUsers.length}
            pageSize={pageSize}
            current={current}
            onChange={onChange}
            className="!mt-5"
          />
        )}
      </div>
    );
  };

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates) {
      setDate([dateStrings[0], dateStrings[1]]);
    }
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > dayjs().endOf('day');
  };

  const getLabel = (value?: MostActiveUsers) => {
    switch (tab) {
      case Tab.POINT:
        return t('number_points', { number: formatNumber(value?.points) });
      case Tab.IDEA:
        return t('number_ideas', { number: formatNumber(value?.ideas) });
      case Tab.POST:
        return t('number_posts', { number: formatNumber(value?.posts) });
      case Tab.CHALLENGE:
        return t('number_challenges', {
          number: formatNumber(value?.challenges),
        });
      case Tab.UPVOTE:
        return t('number_upvotes', { number: formatNumber(value?.upvotes) });
      case Tab.CONTRIBUTION:
        return t('number_contributions', {
          number: formatNumber(value?.contributions),
        });
      case Tab.APPLICATION:
        return t('number_submit', {
          number: formatNumber(value?.applications),
        });
    }
  };

  return (
    <>
      <Card style={{ borderRadius: 20 }} className="space-y-4">
        <div className="text-sm text-gray-900 font-medium">
          {tcommon('top_contributors')}
        </div>
        <div className="flex flex-wrap justify-between items-center my-3">
          <Dropdown
            menu={{
              items: dropdownOptions.map((item) => ({
                ...item,
                onClick: () => setOption(item),
              })),
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button shape="round">
              {option.label}
              <DownOutlined />
            </Button>
          </Dropdown>
          <RangePicker
            picker="month"
            onChange={handleChange}
            defaultValue={[dayjs(date[0]), dayjs(date[1])]}
            disabledDate={disabledDate}
            style={{
              borderRadius: 20,
              width: '250px',
            }}
          />
        </div>
        <Segmented
          value={tab}
          options={
            option.key === 'internal-users'
              ? internalSegmentOptions
              : externalSegmentOptions
          }
          onChange={(value: Tab) => setTab(value)}
        />
        <List />
      </Card>
      <Drawer
        title={
          <div className="flex justify-between">
            <Button
              ghost
              type="text"
              onClick={handleDrawer}
              icon={<DoubleRightOutlined />}
            />
            <Link href={`/user/${selectedUser?.id}`} target="_blank">
              <Button
                type="primary"
                shape="round"
                onClick={handleDrawer}
                icon={<LinkOutlined />}
              >
                {tcommon('user_profile_open_button')}
              </Button>
            </Link>
          </div>
        }
        placement="right"
        closable={false}
        onClose={handleDrawer}
        open={drawer}
        width={screens.md ? '500' : null}
      >
        <UserInfo user={selectedUser} />
      </Drawer>
    </>
  );
};

export default TopContributorsStats;
